import { supabase } from '../lib/supabase';

// Helper function to convert snake_case to camelCase
const toCamelCase = (obj) => {
  if (!obj || typeof obj !== 'object') return obj;
  if (Array.isArray(obj)) return obj?.map(toCamelCase);
  
  return Object.keys(obj)?.reduce((acc, key) => {
    const camelKey = key?.replace(/_([a-z])/g, (_, letter) => letter?.toUpperCase());
    acc[camelKey] = toCamelCase(obj?.[key]);
    return acc;
  }, {});
};

// Helper function to convert camelCase to snake_case
const toSnakeCase = (obj) => {
  if (!obj || typeof obj !== 'object') return obj;
  if (Array.isArray(obj)) return obj?.map(toSnakeCase);
  
  return Object.keys(obj)?.reduce((acc, key) => {
    const snakeKey = key?.replace(/[A-Z]/g, letter => `_${letter?.toLowerCase()}`);
    acc[snakeKey] = toSnakeCase(obj?.[key]);
    return acc;
  }, {});
};

// Get proper redirect URL based on environment
const getRedirectUrl = () => {
  // In production, use the actual domain
  // In development, use localhost with proper port
  const baseUrl = window.location?.origin;
  return `${baseUrl}/buyer-login`;
};

// Buyer Authentication Service
export const buyerAuthService = {
  async signUp(userData) {
    const { data, error } = await supabase?.auth?.signUp({
      email: userData?.email,
      password: userData?.password,
      options: {
        emailRedirectTo: getRedirectUrl(),
        data: {
          full_name: userData?.fullName,
          role: 'buyer',
          company_name: userData?.companyName,
          phone: userData?.phone,
          country: userData?.country,
          business_license_number: userData?.businessLicenseNumber,
          import_license_number: userData?.importLicenseNumber,
          preferred_language: userData?.preferredLanguage || 'en',
          account_type: userData?.accountType || 'individual'
        }
      }
    });
    if (error) throw error;
    return data;
  },

  async signIn(email, password) {
    const { data, error } = await supabase?.auth?.signInWithPassword({ email, password });
    if (error) throw error;
    return data;
  },

  async signOut() {
    const { error } = await supabase?.auth?.signOut();
    if (error) throw error;
  },

  async resetPassword(email) {
    const { data, error } = await supabase?.auth?.resetPasswordForEmail(email);
    if (error) throw error;
    return data;
  }
};

// Buyer Favorites Service
export const buyerFavoritesService = {
  async getAll() {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    const { data, error } = await supabase
      ?.from('buyer_favorites')
      ?.select(`
        *,
        bean:beans(
          id, name_en, name_zh, price_usd_per_kg, available_qty_kg, processing_type, grade,
          country:countries(name_en, name_zh, code),
          images:bean_images(id, image_url, alt_text_en, alt_text_zh, is_primary)
        )
      `)
      ?.eq('buyer_id', user?.id)
      ?.order('created_at', { ascending: false });

    if (error) throw error;
    return toCamelCase(data);
  },

  async add(beanId) {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    const { data, error } = await supabase
      ?.from('buyer_favorites')
      ?.insert({ buyer_id: user?.id, bean_id: beanId })
      ?.select()
      ?.single();

    if (error) throw error;
    return toCamelCase(data);
  },

  async remove(beanId) {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    const { error } = await supabase
      ?.from('buyer_favorites')
      ?.delete()
      ?.eq('buyer_id', user?.id)
      ?.eq('bean_id', beanId);

    if (error) throw error;
    return true;
  },

  async isFavorite(beanId) {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) return false;

    const { data, error } = await supabase
      ?.from('buyer_favorites')
      ?.select('id')
      ?.eq('buyer_id', user?.id)
      ?.eq('bean_id', beanId)
      ?.single();

    return !error && data !== null;
  }
};

// Buyer Communications Service
export const buyerCommunicationsService = {
  async getAll() {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    const { data, error } = await supabase
      ?.from('buyer_communications')
      ?.select(`
        *,
        supplier:suppliers(id, name_en, name_zh, contact_email),
        bean:beans(id, name_en, name_zh),
        messages:buyer_communication_messages(
          id, message, created_at,
          sender:user_profiles(id, full_name)
        )
      `)
      ?.eq('buyer_id', user?.id)
      ?.order('updated_at', { ascending: false });

    if (error) throw error;
    return toCamelCase(data);
  },

  async create(communicationData) {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    const snakeData = toSnakeCase({ ...communicationData, buyerId: user?.id });
    const { data, error } = await supabase
      ?.from('buyer_communications')
      ?.insert(snakeData)
      ?.select()
      ?.single();

    if (error) throw error;
    return toCamelCase(data);
  },

  async addMessage(communicationId, message) {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    const { data, error } = await supabase
      ?.from('buyer_communication_messages')
      ?.insert({
        communication_id: communicationId,
        sender_id: user?.id,
        message
      })
      ?.select()
      ?.single();

    if (error) throw error;

    // Update communication updated_at timestamp
    await supabase
      ?.from('buyer_communications')
      ?.update({ updated_at: new Date()?.toISOString() })
      ?.eq('id', communicationId);

    return toCamelCase(data);
  }
};

// Buyer Orders Service (extends inquiries)
export const buyerOrdersService = {
  async getAll() {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    const { data, error } = await supabase
      ?.from('inquiries')
      ?.select(`
        *,
        bean:beans(
          id, name_en, name_zh, price_usd_per_kg,
          supplier:suppliers(id, name_en, name_zh, contact_email)
        )
      `)
      ?.eq('buyer_id', user?.id)
      ?.order('created_at', { ascending: false });

    if (error) throw error;
    return toCamelCase(data);
  },

  async getById(orderId) {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    const { data, error } = await supabase
      ?.from('inquiries')
      ?.select(`
        *,
        bean:beans(
          id, name_en, name_zh, price_usd_per_kg, description_en, description_zh,
          supplier:suppliers(id, name_en, name_zh, contact_email, contact_phone)
        )
      `)
      ?.eq('id', orderId)
      ?.eq('buyer_id', user?.id)
      ?.single();

    if (error) throw error;
    return toCamelCase(data);
  }
};

// Buyer Query History Service
export const buyerQueryHistoryService = {
  async getAll() {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    const { data, error } = await supabase
      ?.from('buyer_queries')
      ?.select('*')
      ?.eq('email', user?.email)
      ?.order('created_at', { ascending: false });

    if (error) throw error;
    return toCamelCase(data);
  }
};

// Buyer Queries Service (for query history)
export const buyerQueriesService = {
  async getMyQueries() {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    const { data, error } = await supabase
      ?.from('inquiries')
      ?.select(`
        *,
        bean:beans(id, name_en, name_zh, price_usd_per_kg)
      `)
      ?.eq('buyer_id', user?.id)
      ?.order('created_at', { ascending: false });

    if (error) throw error;
    return toCamelCase(data);
  },

  async create(queryData) {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    const snakeData = toSnakeCase({ ...queryData, buyerId: user?.id });
    const { data, error } = await supabase
      ?.from('inquiries')
      ?.insert(snakeData)
      ?.select()
      ?.single();

    if (error) throw error;
    return toCamelCase(data);
  }
};

// Buyer Profile Service
export const buyerProfileService = {
  async getProfile() {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    const { data, error } = await supabase
      ?.from('user_profiles')
      ?.select('*')
      ?.eq('id', user?.id)
      ?.single();

    if (error) throw error;
    return toCamelCase(data);
  },

  async updateProfile(profileData) {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    const snakeData = toSnakeCase(profileData);
    const { data, error } = await supabase
      ?.from('user_profiles')
      ?.update(snakeData)
      ?.eq('id', user?.id)
      ?.select()
      ?.single();

    if (error) throw error;
    return toCamelCase(data);
  }
};